import { useState, useEffect, useCallback, useRef } from 'react';
import type { RateCardSummary, RoleRate, AssignedPerson, UpdateRoleRateRequest, UpdatePersonRateRequest } from '@/types/rateCard.types';
import { calculateMargin } from '@/types/rateCard.types';
import type { PersonBudget } from '@/types/budget.types';
import { RateCardStorageService } from '@/services/rateCardStorage.service';
import { useBudgetData } from './use-budget-data';
import { getDataMode, getApiBaseUrl } from '@/lib/data-service';
import { apiFetch } from '@/lib/api-error-handler';

interface BackendPersonRate {
  employeeId: number;
  employeeName: string;
  roleId: number;
  roleName: string;
  costRateOverride: number | null;
  billRateOverride: number | null;
  dailyCostRateOverride: number | null;
  dailyBillRateOverride: number | null;
  defaultCostRate: number | null;
  defaultBillRate: number | null;
  defaultDailyCostRate: number | null;
  defaultDailyBillRate: number | null;
}

interface BackendRateCardEntry {
  roleId: number;
  roleName: string;
  defaultCostRate: number;
  defaultBillRate: number;
  defaultDailyCostRate: number | null;
  defaultDailyBillRate: number | null;
  projectRoleRateId: number | null;
  projectCostOverride: number | null;
  projectBillOverride: number | null;
  projectDailyCostOverride: number | null;
  projectDailyBillOverride: number | null;
  effectiveCostRate: number;
  effectiveBillRate: number;
  effectiveDailyCostRate: number | null;
  effectiveDailyBillRate: number | null;
  source: string;
}

function transformBackendToRoleRates(entries: BackendRateCardEntry[]): RoleRate[] {
  return entries.map((entry) => ({
    roleId: `role-${entry.roleId}`,
    roleName: entry.roleName,
    hourlyCostRate: entry.effectiveCostRate,
    hourlyBillRate: entry.effectiveBillRate,
    dailyCostRate: entry.effectiveDailyCostRate ?? entry.defaultDailyCostRate ?? 0,
    dailyBillRate: entry.effectiveDailyBillRate ?? entry.defaultDailyBillRate ?? 0,
    margin: calculateMargin(entry.effectiveBillRate, entry.effectiveCostRate),
    effectiveFrom: new Date().toISOString(),
    source: (entry.source || 'Org Default') as 'Org Default' | 'Project Override' | 'Person Override',
    assignedPeople: [],
  }));
}

function computeSummary(roleRates: RoleRate[]): RateCardSummary {
  if (roleRates.length === 0) {
    return {
      averageHourlyBillRate: 0,
      averageHourlyCostRate: 0,
      averageDailyBillRate: 0,
      averageDailyCostRate: 0,
      averageMargin: 0,
      roleCount: 0,
      lastUpdated: new Date().toISOString(),
      lastUpdatedBy: 'System',
    };
  }

  const totalHourlyBill = roleRates.reduce((sum, r) => sum + r.hourlyBillRate, 0);
  const totalHourlyCost = roleRates.reduce((sum, r) => sum + r.hourlyCostRate, 0);
  const totalDailyBill = roleRates.reduce((sum, r) => sum + r.dailyBillRate, 0);
  const totalDailyCost = roleRates.reduce((sum, r) => sum + r.dailyCostRate, 0);

  const avgHourlyBill = totalHourlyBill / roleRates.length;
  const avgHourlyCost = totalHourlyCost / roleRates.length;

  return {
    averageHourlyBillRate: Math.round(avgHourlyBill * 100) / 100,
    averageHourlyCostRate: Math.round(avgHourlyCost * 100) / 100,
    averageDailyBillRate: Math.round((totalDailyBill / roleRates.length) * 100) / 100,
    averageDailyCostRate: Math.round((totalDailyCost / roleRates.length) * 100) / 100,
    averageMargin: calculateMargin(avgHourlyBill, avgHourlyCost),
    roleCount: roleRates.length,
    lastUpdated: new Date().toISOString(),
    lastUpdatedBy: 'Current User',
  };
}

export const useRateCard = (projectId: string) => {
  const [summary, setSummary] = useState<RateCardSummary>({
    averageHourlyBillRate: 0,
    averageHourlyCostRate: 0,
    averageDailyBillRate: 0,
    averageDailyCostRate: 0,
    averageMargin: 0,
    roleCount: 0,
    lastUpdated: new Date().toISOString(),
    lastUpdatedBy: 'System',
  });
  const [roleRates, setRoleRates] = useState<RoleRate[]>([]);
  const [loading, setLoading] = useState(true);
  const backendEntriesRef = useRef<BackendRateCardEntry[]>([]);

  const mode = getDataMode();

  const { data: budgetData } = useBudgetData(parseInt(projectId));

  const loadDemoData = useCallback(() => {
    const allocatedRoles = budgetData?.resourceBudget?.byRole?.map(role => role.role) || [];
    const allocatedPeople = budgetData?.resourceBudget?.byPerson?.map(person => ({
      personId: person.personId.toString(),
      personName: person.personName,
      role: person.role,
    })) || [];

    const filter = allocatedRoles.length > 0 ? { allocatedRoles, allocatedPeople } : undefined;

    const summaryData = RateCardStorageService.getRateCardSummary(projectId, filter);
    const roleRatesData = RateCardStorageService.getAllRoleRates(projectId, filter);

    setSummary(summaryData);
    setRoleRates(roleRatesData);
  }, [projectId, budgetData]);

  const loadLiveData = useCallback(async () => {
    const response = await apiFetch<BackendRateCardEntry[]>(
      `${getApiBaseUrl()}/ratecardentries/by-project/${projectId}`
    );

    let entries = Array.isArray(response) ? response : [];

    const resourceBudgetRoles = budgetData?.resourceBudget?.byRole;
    if (resourceBudgetRoles && resourceBudgetRoles.length > 0) {
      const budgetRoleIds = new Set(
        resourceBudgetRoles
          .filter(r => r.roleId != null)
          .map(r => r.roleId!)
      );
      const budgetRoleNames = new Set(
        resourceBudgetRoles.map(r => r.role.toLowerCase())
      );

      entries = entries.filter(entry =>
        budgetRoleIds.has(entry.roleId) ||
        budgetRoleNames.has(entry.roleName.toLowerCase())
      );
    }

    backendEntriesRef.current = entries;
    const rates = transformBackendToRoleRates(entries);

    let personRateOverrides: BackendPersonRate[] = [];
    try {
      const prResponse = await apiFetch<BackendPersonRate[]>(
        `${getApiBaseUrl()}/ratecardentries/by-project/${projectId}/person-rates`
      );
      personRateOverrides = Array.isArray(prResponse) ? prResponse : [];
    } catch {
      personRateOverrides = [];
    }

    const allocatedPeople = budgetData?.resourceBudget?.byPerson || [];
    if (allocatedPeople.length > 0) {
      for (const rate of rates) {
        const matchingPeople = allocatedPeople.filter(
          p => p.role.toLowerCase() === rate.roleName.toLowerCase()
        );
        rate.assignedPeople = matchingPeople.map((person: PersonBudget): AssignedPerson => {
          const override = personRateOverrides.find(
            pr => pr.employeeId === person.personId && pr.roleName.toLowerCase() === rate.roleName.toLowerCase()
          );

          const baseCost = person.costRate || rate.hourlyCostRate;
          const baseBill = rate.hourlyBillRate;
          const baseDailyCost = baseCost * 8;
          const baseDailyBill = rate.dailyBillRate;

          const effectiveCost = override?.costRateOverride ?? baseCost;
          const effectiveBill = override?.billRateOverride ?? baseBill;
          const effectiveDailyCost = override?.dailyCostRateOverride ?? baseDailyCost;
          const effectiveDailyBill = override?.dailyBillRateOverride ?? baseDailyBill;

          return {
            personId: person.personId.toString(),
            personName: person.personName,
            hourlyCostRate: effectiveCost,
            hourlyBillRate: effectiveBill,
            dailyCostRate: effectiveDailyCost,
            dailyBillRate: effectiveDailyBill,
            margin: calculateMargin(effectiveBill, effectiveCost),
            effectiveFrom: new Date().toISOString(),
            source: override ? 'Person Override' : 'Org Default',
          };
        });
      }
    }

    const summaryData = computeSummary(rates);

    setRoleRates(rates);
    setSummary(summaryData);
  }, [projectId, budgetData]);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      if (mode === 'live') {
        await loadLiveData();
      } else {
        loadDemoData();
      }
    } catch (error) {
      console.error('Error loading rate card data:', error);
    } finally {
      setLoading(false);
    }
  }, [mode, loadLiveData, loadDemoData]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const updateRoleRate = (request: UpdateRoleRateRequest) => {
    if (mode === 'live') {
      const dbRoleId = parseInt(request.roleId.replace('role-', ''), 10);
      const entry = backendEntriesRef.current.find(e => e.roleId === dbRoleId);
      if (!entry) return false;

      const payload = {
        costRateOverride: request.hourlyCostRate,
        billRateOverride: request.hourlyBillRate,
        dailyCostRateOverride: request.dailyCostRate,
        dailyBillRateOverride: request.dailyBillRate,
      };

      apiFetch(`${getApiBaseUrl()}/ratecardentries/by-project/${projectId}/role/${dbRoleId}`, {
        method: 'PUT',
        body: JSON.stringify(payload),
      }).then(() => loadData()).catch(err => console.error('Error updating role rate:', err));

      return true;
    }
    try {
      RateCardStorageService.updateRoleRate(projectId, request);
      loadData();
      return true;
    } catch (error) {
      console.error('Error updating role rate:', error);
      return false;
    }
  };

  const updatePersonRate = (request: UpdatePersonRateRequest) => {
    if (mode === 'live') {
      const dbRoleId = parseInt(request.roleId.replace('role-', ''), 10);
      const employeeId = parseInt(request.personId, 10);

      const payload = {
        costRateOverride: request.hourlyCostRate,
        billRateOverride: request.hourlyBillRate,
        dailyCostRateOverride: request.dailyCostRate,
        dailyBillRateOverride: request.dailyBillRate,
      };

      apiFetch(`${getApiBaseUrl()}/ratecardentries/by-project/${projectId}/person/${employeeId}/role/${dbRoleId}`, {
        method: 'PUT',
        body: JSON.stringify(payload),
      }).then(() => loadData()).catch(err => console.error('Error updating person rate:', err));

      return true;
    }
    try {
      RateCardStorageService.updatePersonRate(projectId, request);
      loadData();
      return true;
    } catch (error) {
      console.error('Error updating person rate:', error);
      return false;
    }
  };

  const clearAllData = () => {
    if (mode === 'live') {
      console.warn('[useRateCard] Live mode clear not yet implemented via API');
      return false;
    }
    try {
      RateCardStorageService.clearProjectRates(projectId);
      loadData();
      return true;
    } catch (error) {
      console.error('Error clearing data:', error);
      return false;
    }
  };

  return {
    summary,
    roleRates,
    loading,
    updateRoleRate,
    updatePersonRate,
    clearAllData,
    refresh: loadData,
  };
};
